import type { Event } from '@patsnap/tahiti-core';
import type { SpecChapter, ParagraphIssueGroup } from '../types';

/**
 * 章节加载完成事件
 */
export interface ChaptersLoadedEvent {
    chapters: SpecChapter[];
}

/**
 * 章节检查开始事件
 */
export interface ChapterCheckStartedEvent {
    chapterType: string;
    chapterIndex: number;
    totalChapters: number;
}

/**
 * 章节问题发现事件
 */
export interface ChapterIssuesFoundEvent {
    chapterType: string;
    issueGroups: ParagraphIssueGroup[];
    chapterIndex: number;
    totalChapters: number;
}

/**
 * 核稿流程完成事件
 */
export interface ProofReadingCompletedEvent {
    // 不携带数据，仅作为完成信号
}


/**
 * 执行状态变化事件
 */
export interface ExecutionStatusChangedEvent {
    isExecuting: boolean;
}

/**
 * Issues 加载状态变化事件
 */
export interface IssuesLoadingStatusChangedEvent {
    isLoadingIssues: boolean;
}

/**
 * 核稿编排服务
 * 负责协调章节获取和内容检查流程，并提供事件机制供视图层订阅
 */
export const ProofReadingOrchestrationService = Symbol.for('ProofReadingOrchestrationService');

/**
 * 当前执行状态信息
 */
export interface CurrentExecutionState {
    isExecuting: boolean;
    currentChapterName?: string;
    currentChapterIndex?: number;
    totalChapters?: number;
    totalIssuesFound?: number;
}

export interface ProofReadingOrchestrationService {
    /**
     * 加载章节信息
     */
    loadChapters(): Promise<SpecChapter[]>;

    /**
     * 查询问题
     */
    findIssues(options: {
        chapters: SpecChapter[];
        regenerate?: boolean;
    }): Promise<void>;

    /**
     * 中断当前的查询问题流程
     */
    abortFindIssues(): void;

    /**
     * 获取当前执行状态的详细信息
     * 用于事件监听器注册时同步当前状态
     */
    getCurrentExecutionState(): CurrentExecutionState;

    // 事件订阅接口
    readonly onChaptersLoad: Event<void>;
    readonly onDidChaptersLoad: Event<ChaptersLoadedEvent>;
    readonly onChapterCheckStarted: Event<ChapterCheckStartedEvent>;
    readonly onChapterIssuesFound: Event<ChapterIssuesFoundEvent>;
    readonly onProofReadingCompleted: Event<ProofReadingCompletedEvent>;
    readonly onProofReadingError: Event<Error>;
    readonly onExecutionStatusChanged: Event<ExecutionStatusChangedEvent>;
    readonly onIssuesLoadingStatusChanged: Event<IssuesLoadingStatusChangedEvent>;
}
